const LEVELS = ['cheap', 'normal', 'expensive'];
const isFiniteNumber = value => typeof value === 'number' && Number.isFinite(value);

/**
 * Group available dates by price level.
 *
 * @param {Object} availability - keyed by date string, each value is a day record
 *   with shape: { available, minPrice: number|null, priceLevel: 'cheap'|'normal'|'expensive'|null }
 *   where `minPrice` is the flat day-level price (sourced from `display_price`).
 * @returns {{ cheap: Date[], normal: Date[], expensive: Date[] }}
 */
export const groupDatesByPriceLevel = availability => {
  const result = { cheap: [], normal: [], expensive: [] };
  if (!availability) return result;

  Object.entries(availability).forEach(([dateKey, data]) => {
    if (!data?.available || !data?.priceLevel) return;
    const level = data.priceLevel;
    if (!(level in result)) return;
    result[level].push(new Date(`${dateKey}T00:00:00`));
  });

  return result;
};

const rangesFromDays = availability => {
  const result = { cheap: null, normal: null, expensive: null };
  if (!availability) return result;

  Object.values(availability).forEach(day => {
    if (!day || !day.available) return;
    const level = day.priceLevel;
    if (!LEVELS.includes(level)) return;
    const price = day.minPrice;
    if (!isFiniteNumber(price)) return;

    const current = result[level];
    result[level] = current
      ? { min: Math.min(current.min, price), max: Math.max(current.max, price) }
      : { min: price, max: price };
  });

  return result;
};

const apiRange = (priceLevels, level) => {
  const range = priceLevels?.[level];
  if (!isFiniteNumber(range?.min) || !isFiniteNumber(range?.max)) return null;
  if (range.min > range.max) return null;

  return { min: range.min, max: range.max };
};

/**
 * Select the active leg's API ranges only when they describe the displayed window.
 * Stale metadata is expected while cached or degraded navigation settles; returning
 * null lets the legend derive a truthful fallback from the current window's days.
 */
export const getPriceLevelsForWindow = (meta, window, leg) => {
  if (!meta || !window || meta.from !== window.from || meta.to !== window.to) return null;
  return meta.priceLevels?.[leg] ?? null;
};

/**
 * Real price range of every level present in the window.
 *
 * A level is only reported when the window's own days put a priced day in it — the
 * legend must never outlive the days it describes (while a newly navigated month is
 * loading, `meta` still holds the previous window's ranges). For a level that *is*
 * present, `meta.price_levels` wins when available: the API builds it from the very
 * days it returned, so both bounds are prices a selectable day carries.
 *
 * @param {Object} availability - keyed by date string (same shape as groupDatesByPriceLevel)
 * @param {Object} [priceLevels] - camelized `meta.priceLevels[<leg>]`, e.g.
 *   `{ cheap: { min, max, days } }`
 * @returns {{ cheap: {min: number, max: number}|null, normal: ..., expensive: ... }}
 */
export const getPriceRangeByLevel = (availability, priceLevels = null) => {
  const fromDays = rangesFromDays(availability);

  return LEVELS.reduce((result, level) => {
    if (fromDays[level] === null) return { ...result, [level]: null };

    return { ...result, [level]: apiRange(priceLevels, level) ?? fromDays[level] };
  }, {});
};
